import { retriesRequest } from "@/utils/queryClient";
import { ServerConnectionsService } from "@/services/ServerConnections";
import { AuthConnectionsService } from "@/services/AuthConnections";
import { AdminService as LicenseAdminService } from "@/licensegenerated";
import {
  WorkflowService,
  PaymentsService,
  IntegrationsService,
  UsersService,
  KnowledgeBasesV2Service,
  ConversationsService,
  UploadService,
  InternalService,
  StatusService,
  TemplatesService,
  RunsService,
  TriggersService,
  AssistantsService,
  AnalyticsService,
  LabellingTasksService,
  ProvidersService,
  PluginsService,
  AppsService,
  AdminService,
  RolesService,
  PermissionsService,
  TagsService,
  EvaluationService,
  CaseManagementService,
  DeploymentsService,
  BillingService,
  TablesService,
  InsightsService,
  McpConnectionsService,
  FeatureFlagsService,
} from "@/generated";
import type {
  GetEnabledBetaFeaturesData,
  GetGroupData,
  GetMeData,
  GetMyGroupsData,
  GetMyPermissionsData,
  GetTenantData,
  Options,
  StorageStatusData,
} from "@/generated";
import { contextOr } from "@/services/AuthorizationContext/firstLoad";
import { runHeuristicEvaluationJob } from "@/services/promptHeuristicJobs";

import { UseQueryOptions, useQuery, QueryClient } from "@tanstack/react-query";

export enum DATA_KEYS {
  SERVER_CONNECTIONS = "SERVER_CONNECTIONS",
  SERVER_CONNECTION_SENDERS = "SERVER_CONNECTION_SENDERS",
  AUTH_ENVIRONMENT = "AUTH_ENVIRONMENT",
  AUTH_SETUP = "AUTH_SETUP",
  AUTH_CONNECTIONS = "AUTH_CONNECTIONS",
  AUTH_MIGRATION = "AUTH_MIGRATION",
  AUTH_MIGRATION_EMAILS = "AUTH_MIGRATION_EMAILS",
  AUTH_TRANSITION = "AUTH_TRANSITION",
  AUTH_LINKED_ACCOUNTS = "AUTH_LINKED_ACCOUNTS",
  LICENSE_ORGANIZATIONS = "LICENSE_ORGANIZATIONS",
  LICENSE_ORGANIZATION_GROUPS = "LICENSE_ORGANIZATION_GROUPS",
  LICENSE_DEFAULT_OVERAGE_TIERS = "LICENSE_DEFAULT_OVERAGE_TIERS",
  LICENSE_ORGANIZATION_PERIOD_USAGE = "LICENSE_ORGANIZATION_PERIOD_USAGE",
  LICENSE_ORGANIZATION_BILLING_AUDIT = "LICENSE_ORGANIZATION_BILLING_AUDIT",
  SAAS_TENANT_BILLING_INFORMATION = "SAAS_TENANT_BILLING_INFORMATION",
  SAAS_TENANT_PERIOD_USAGE = "SAAS_TENANT_PERIOD_USAGE",
  SAAS_TENANT_BILLING_AUDIT = "SAAS_TENANT_BILLING_AUDIT",
  SAAS_TENANT_AUDIT_LOGS = "SAAS_TENANT_AUDIT_LOGS",
  SAAS_TENANT_AUDIT_LOG_FACETS = "SAAS_TENANT_AUDIT_LOG_FACETS",
  CHANGELOG = "CHANGELOG",
  CONVERSATION = "CONVERSATION",
  CONVERSATION_RATED_MESSAGES = "CONVERSATION_RATED_MESSAGES",
  CONVERSATIONS = "CONVERSATIONS",
  CONVERSATIONS_ID = "CONVERSATIONS_ID",
  WORKFLOWS = "WORKFLOWS",
  WORKFLOWS_ID = "WORKFLOWS_ID",
  WORKFLOWS_HANDLED_BY = "WORKFLOWS_HANDLED_BY",
  INBOXES = "INBOXES",
  INBOXES_ID = "INBOXES_ID",
  CASES = "CASES",
  CASES_ID = "CASES_ID",
  TASKS = "TASKS",
  TASK_DETAIL = "TASK_DETAIL",
  TAGS = "TAGS",
  WORKFLOW_DEPENDENCIES = "WORKFLOW_DEPENDENCIES",
  TEMPLATES = "TEMPLATES",
  TEMPLATES_V2 = "TEMPLATES_V2",
  TEMPLATE_V2 = "TEMPLATE_V2",
  TEMPLATES_TRENDING = "TEMPLATES_TRENDING",
  INTEGRATIONS = "INTEGRATIONS",
  INTEGRATIONS_CONFIG = "INTEGRATIONS_CONFIG",
  MCP_IDENTITY = "MCP_IDENTITY",
  NODES = "NODES",
  NODE_DISPLAY = "NODE_DISPLAY",
  MODELS = "MODELS",
  WORKFLOW_RUNS = "WORKFLOW_RUNS",
  RUN = "RUN",
  RUN_IO = "RUN_IO",
  RUN_WORKFLOW = "RUN_WORKFLOW",
  ALL_RUNS = "ALL_RUNS",
  KNOWLEDGE_BASES_TYPES = "KNOWLEDGE_BASES_TYPES",
  AVAILABLE_TRIGGERS = "AVAILABLE_TRIGGERS",
  TRIGGERS = "TRIGGERS",
  TRIGGERS_CONFIG = "TRIGGERS_CONFIG",
  TRIGGERS_SLACK_INFO = "TRIGGERS_SLACK_INFO",
  TRIGGER_DEBUG = "TRIGGER_DEBUG",
  GROUP_TRIGGER_EVENTS = "GROUP_TRIGGER_EVENTS",
  APIKEYS = "APIKEYS",
  APIKEY_ID = "APIKEY_ID",
  INTEGRATIONS_AVAILABLE = "INTEGRATIONS_AVAILABLE",
  GROUP = "GROUP",
  GROUP_USERS = "GROUP_USERS",
  GROUPS = "GROUPS",
  MY_GROUPS = "MY_GROUPS",
  ALL_GROUPS = "ALL_GROUPS",
  USERS = "USERS",
  USER = "USER",
  USER_ANONYMOUS = "USER_ANONYMOUS",
  USER_DETAILS = "USER_DETAILS",
  TENANT = "TENANT",
  BETA_FEATURES = "BETA_FEATURES",
  BETA_FEATURE_STATES = "BETA_FEATURE_STATES",
  GENIE_ASSISTANT = "GENIE_ASSISTANT",
  INVITATION = "INVITATION",
  KNOWLEDGE_BASES_V2 = "KNOWLEDGE_BASES_V2",
  KNOWLEDGE_BASES_V2_ID = "KNOWLEDGE_BASES_V2_ID",
  KNOWLEDGE_BASE_DOCUMENTS = "KNOWLEDGE_BASE_DOCUMENTS",
  TABLES = "TABLES",
  TABLES_ID = "TABLES_ID",
  TABLES_WRITE_SUMMARY = "TABLES_WRITE_SUMMARY",
  TABLES_IMPORT_PLAN = "TABLES_IMPORT_PLAN",
  TABLE_ROWS = "TABLE_ROWS",
  TABLE_STATS = "TABLE_STATS",
  KNOWLEDGE_BASE_INGESTION_JOBS = "KNOWLEDGE_BASE_INGESTION_JOBS",
  KB_SYNCS = "KB_SYNCS",
  AZURE_INTEGRATION_NANGO_USERNAME = "AZURE_INTEGRATION_NANGO_USERNAME",
  FILE_METADATA = "FILE_METADATA",
  RUNS_SEARCH = "RUNS_SEARCH",
  ASSISTANTS = "ASSISTANTS",
  ASSISTANT_ID = "ASSISTANT_ID",
  // The agent settings form, keyed by the noxus_settings version it was
  // fetched for — a capability update changes which shape the builder renders.
  AGENT_FORM = "agent-data",
  ANALYTICS_TENANT_METADATA = "ANALYTICS_TENANT_METADATA",
  ANALYTICS_BILLING_METADATA = "ANALYTICS_BILLING_METADATA",
  ANALYTICS_CHAT_METADATA = "ANALYTICS_CHAT_METADATA",
  ANALYTICS_WORKFLOW_METADATA = "ANALYTICS_WORKFLOW_METADATA",
  ANALYTICS_KB_METADATA = "ANALYTICS_KB_METADATA",
  ANALYTICS_WORKSPACE = "ANALYTICS_WORKSPACE",
  TENANT_ANALYTICS = "TENANT_ANALYTICS",
  WORKFLOW_ANALYTICS = "WORKFLOW_ANALYTICS",
  ASSISTANT_ANALYTICS = "ASSISTANT_ANALYTICS",
  KB_ANALYTICS = "KB_ANALYTICS",
  API_ANALYTICS_METADATA = "API_ANALYTICS_METADATA",
  INFRASTRUCTURE_ANALYTICS_METADATA = "INFRASTRUCTURE_ANALYTICS_METADATA",
  WORKFLOW_LOG_COLUMNS = "WORKFLOW_LOG_COLUMNS",
  LABELLING_TASKS = "LABELLING_TASKS",
  WORKSHOP_RECENT = "WORKSHOP_RECENT",
  WORKFLOW_VERSIONS = "WORKFLOW_VERSIONS",
  WORKFLOW_VERSION = "WORKFLOW_VERSION",
  WORKFLOW_VERSION_USAGES = "WORKFLOW_VERSION_USAGES",
  ASSISTANT_VERSION_USAGES = "ASSISTANT_VERSION_USAGES",
  TRIGGERS_COMPATIBILITY = "TRIGGERS_COMPATIBILITY",
  WORKFLOW_IS_VALID = "WORKFLOW_IS_VALID",
  PROVIDERS = "PROVIDERS",
  PLUGINS = "PLUGINS",
  PLUGIN_CATALOG = "PLUGIN_CATALOG",
  MARKETPLACE_PLUGINS = "MARKETPLACE_PLUGINS",
  PLUGINS_MISSING_CONFIG_CHECK = "PLUGINS_MISSING_CONFIG_CHECK",
  APPS = "APPS",
  ASSISTANT_VERSIONS = "ASSISTANT_VERSIONS",
  ASSISTANT_CAPABILITIES = "ASSISTANT_CAPABILITIES",
  ASSISTANT_CHANGES = "ASSISTANT_CHANGES",
  NEW_ASSISTANT_CHANGES = "NEW_ASSISTANT_CHANGES",
  ASSISTANT_TOOLS = "ASSISTANT_TOOLS",
  ASSISTANT_CAPABILITY_UPDATES = "ASSISTANT_CAPABILITY_UPDATES",
  ASSISTANT_VERSION = "ASSISTANT_VERSION",
  ASSISTANT_TRIGGER_CONFIG = "ASSISTANT_TRIGGER_CONFIG",
  DEPLOYMENT_CONFIG = "DEPLOYMENT_CONFIG",
  DEPLOYMENT_APP = "DEPLOYMENT_APP",
  DEPLOYMENT_APP_BY_ALIAS = "DEPLOYMENT_APP_BY_ALIAS",
  LIST_DEPLOYMENTS = "LIST_DEPLOYMENTS",
  ASSISTANT_TRIGGER_CUSTOMIZATION = "ASSISTANT_TRIGGER_CUSTOMIZATION",
  MIME_TYPES = "MIME_TYPES",
  SERVER_SETTINGS = "SERVER_SETTINGS",
  KB_HEALTH_MANIFEST = "KB_HEALTH_MANIFEST",
  NODE_INPUTS = "NODE_INPUTS",
  NODE_LAST_INPUTS = "NODE_LAST_INPUTS",
  EMBEDDINGS = "EMBEDDINGS",
  TYPES_INFORMATION = "TYPES_INFORMATION",
  MODEL_CONFIGURATIONS = "MODEL_CONFIGURATIONS",
  EMBEDDING_CONFIGURATIONS = "EMBEDDING_CONFIGURATIONS",
  RERANKER_CONFIGURATIONS = "RERANKER_CONFIGURATIONS",
  DECISION_MODEL_CONFIGURATIONS = "DECISION_MODEL_CONFIGURATIONS",
  PROVIDER_DEFINITIONS = "PROVIDER_DEFINITIONS",
  EVALUATORS = "EVALUATORS",
  EVALUATOR = "EVALUATOR",
  EVALUATOR_DEFINITIONS = "EVALUATOR_DEFINITIONS",
  WORKFLOW_EVALUATOR_DEFINITIONS = "WORKFLOW_EVALUATOR_DEFINITIONS",
  AGENT_EVALUATOR_DEFINITIONS = "AGENT_EVALUATOR_DEFINITIONS",
  TEST_CASES = "TEST_CASES",
  TEST_CASE = "TEST_CASE",
  TEST_CASE_RUNS = "TEST_CASE_RUNS",
  BILLING = "BILLING",
  BILLING_CURRENCY = "BILLING_CURRENCY",
  BILLING_LIMITS = "BILLING_LIMITS",
  BILLING_LIMIT_PROVIDERS = "BILLING_LIMIT_PROVIDERS",
  BILLING_TOPUPS = "BILLING_TOPUPS",
  TOOL_METADATA_SCHEMAS = "TOOL_METADATA_SCHEMAS",
  STORAGE_STATUS = "STORAGE_STATUS",
  TEST_SUITES = "TEST_SUITES",
  TEST_SUITE = "TEST_SUITE",
  AGENT_TEST_SUITES = "AGENT_TEST_SUITES",
  EVALUATION_RUNS = "EVALUATION_RUNS",
  EVALUATION_RUN_REPORT = "EVALUATION_RUN_REPORT",
  HEURISTICS_EVALUATION = "HEURISTICS_EVALUATION",
  TEST_SUITE_EVALUATION_RESULTS = "TEST_SUITE_EVALUATION_RESULTS",
  ROLES_ORGANIZATION = "ROLES_ORGANIZATION",
  ROLES_WORKSPACE = "ROLES_WORKSPACE",
  // The Roles page's lists, under the keys its own queries have always used.
  ROLES_PAGE_WORKSPACE = "groupRoles",
  ROLES_PAGE_ORGANIZATION = "tenantRoles",
  MY_PERMISSIONS = "MY_PERMISSIONS",
  MODEL_PRESETS = "model-presets",
  INSIGHTS_TOP_TOPICS = "INSIGHTS_TOP_TOPICS",
  INSIGHTS_NOTICED = "INSIGHTS_NOTICED",
  INSIGHTS_SENTIMENT = "INSIGHTS_SENTIMENT",
  INSIGHTS_CSAT = "INSIGHTS_CSAT",
  INSIGHTS_RATING_DRIVERS = "INSIGHTS_RATING_DRIVERS",
  INSIGHTS_CONVERSATION_FUNNEL = "INSIGHTS_CONVERSATION_FUNNEL",
  INSIGHTS_BOOTSTRAP = "INSIGHTS_BOOTSTRAP",
  INSIGHTS_CUSTOM = "INSIGHTS_CUSTOM",
  INSIGHTS_CALCULATING = "INSIGHTS_CALCULATING",
  INSIGHTS_CONVERSATIONS = "INSIGHTS_CONVERSATIONS",
}

interface IQueryConfig<
  TData,
  TKey extends keyof typeof DATA_KEYS,
  TParams extends any[],
> {
  queryFn: (...args: TParams) => Promise<{ data: TData }>;
  queryKeyPrefix?: TKey;
  // Merge React Query's AbortSignal into the queryFn's options argument
  passSignal?: boolean;
  options?: Partial<UseQueryOptions<TData>>;
}

export function dataWrapper<T>(
  callable: (...args: any[]) => Promise<{ data: T }>
) {
  async function call_callable<A extends any[]>(...args: A): Promise<T> {
    const r = await callable(...args);
    return r.data;
  }
  return call_callable;
}

// Data the app shell reads on every page. Its writes invalidate it, so the
// staleTime only stops refetching it on each window focus and each new mount
// (a table row's menu, a second sidebar widget). Not here: billing, which usage
// moves on the server with no write in this client, and the user, whose
// settings several writes spread into their request body (a stale copy would
// be written back).
const SHELL_DATA = { options: { staleTime: 60_000 } };

const queryConfigs = {
  SERVER_CONNECTIONS: { queryFn: ServerConnectionsService.list },
  SERVER_CONNECTION_SENDERS: {
    queryFn: (id: string) => ServerConnectionsService.senders(id),
  },
  AUTH_ENVIRONMENT: { queryFn: AuthConnectionsService.environment },
  AUTH_SETUP: { queryFn: AuthConnectionsService.setupStatus },
  AUTH_CONNECTIONS: { queryFn: AuthConnectionsService.list },
  AUTH_MIGRATION: { queryFn: AuthConnectionsService.migration },
  AUTH_MIGRATION_EMAILS: { queryFn: AuthConnectionsService.migrationEmails },
  AUTH_TRANSITION: { queryFn: AuthConnectionsService.transitionStatus },
  AUTH_LINKED_ACCOUNTS: { queryFn: AuthConnectionsService.accounts },
  LICENSE_ORGANIZATIONS: {
    queryFn: LicenseAdminService.getOrganizations,
    queryKeyPrefix: "ADMIN_ORGANIZATIONS",
  },
  LICENSE_ORGANIZATION_GROUPS: {
    queryFn: LicenseAdminService.getOrganizationGroups,
    queryKeyPrefix: "ADMIN_ORGANIZATIONS",
  },
  LICENSE_ORGANIZATION_PERIOD_USAGE: {
    queryFn: LicenseAdminService.getOrganizationPeriodUsage,
    queryKeyPrefix: "ADMIN_ORGANIZATION",
  },
  LICENSE_DEFAULT_OVERAGE_TIERS: {
    queryFn: LicenseAdminService.getDefaultOverageTiers,
    queryKeyPrefix: "ADMIN_ORGANIZATION",
  },
  LICENSE_ORGANIZATION_BILLING_AUDIT: {
    queryFn: LicenseAdminService.getOrganizationBillingAudit,
    queryKeyPrefix: "ADMIN_ORGANIZATION",
  },
  SAAS_TENANT_BILLING_INFORMATION: {
    queryFn: LicenseAdminService.getCloudOrganizationBillingInformation,
    queryKeyPrefix: "ADMIN_TENANT_BILLING",
  },
  SAAS_TENANT_PERIOD_USAGE: {
    queryFn: LicenseAdminService.getTenantPeriodUsage,
    queryKeyPrefix: "ADMIN_TENANT_BILLING",
  },
  SAAS_TENANT_BILLING_AUDIT: {
    queryFn: LicenseAdminService.getTenantBillingAudit,
    queryKeyPrefix: "ADMIN_TENANT_BILLING",
  },
  SAAS_TENANT_AUDIT_LOGS: {
    queryFn: AdminService.getTenantAuditLogs,
    queryKeyPrefix: "ADMIN_TENANT_AUDIT",
  },
  SAAS_TENANT_AUDIT_LOG_FACETS: {
    queryFn: AdminService.getTenantAuditLogFacets,
    queryKeyPrefix: "ADMIN_TENANT_AUDIT",
  },
  CHANGELOG: {
    queryFn: InternalService.getChangelog,
    options: {
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  },
  CONVERSATION: {
    queryFn: ConversationsService.getConversation,
  },
  CONVERSATION_RATED_MESSAGES: {
    queryFn: ConversationsService.listRatedMessages,
  },
  CONVERSATIONS: {
    queryFn: ConversationsService.listConversations,
  },
  CONVERSATIONS_ID: {
    queryFn: ConversationsService.getConversation,
  },
  TEMPLATES: {
    queryFn: TemplatesService.getTemplates,
  },
  TEMPLATES_V2: {
    queryFn: TemplatesService.getTemplatesV2,
  },
  TEMPLATES_CATEGORIES: {
    queryFn: TemplatesService.getTemplateCategories,
  },
  TEMPLATES_TRENDING: {
    queryFn: TemplatesService.getTrendingTemplates,
  },
  TEMPLATE: {
    queryFn: TemplatesService.getTemplate,
  },
  TEMPLATE_V2: {
    queryFn: TemplatesService.getTemplateV2,
  },
  TEMPLATE_WORKFLOW: {
    queryFn: TemplatesService.getTemplateAsWorkflow,
  },
  WORKFLOWS: {
    queryFn: WorkflowService.getWorkflows,
  },
  WORKFLOWS_HANDLED_BY: {
    queryFn: WorkflowService.getWorkflowHandledBy,
  },
  WORKFLOW_VERSIONS: {
    queryFn: WorkflowService.getWorkflowVersions,
  },
  WORKFLOW_VERSION: {
    queryFn: WorkflowService.getWorkflowVersion,
  },
  WORKFLOW_VERSION_USAGES: {
    queryFn: WorkflowService.getWorkflowVersionUsages,
  },
  ASSISTANT_VERSION_USAGES: {
    queryFn: AssistantsService.getAssistantVersionUsages,
  },
  TRIGGERS_COMPATIBILITY: {
    queryFn: TriggersService.getTriggersCompatibility,
  },
  WORKFLOWS_PAGINATED: {
    queryFn: WorkflowService.getWorkflows,
  },
  WORKFLOWS_ID: {
    queryFn: WorkflowService.getWorkflow,
  },
  INBOXES: {
    queryFn: CaseManagementService.listInboxes,
  },
  INBOXES_ID: {
    queryFn: CaseManagementService.getInbox,
  },
  CASES: {
    queryFn: CaseManagementService.listCases,
  },
  CASES_ID: {
    queryFn: CaseManagementService.getCase,
  },
  TASKS: {
    queryFn: CaseManagementService.listTasks,
  },
  TASK_DETAIL: {
    queryFn: CaseManagementService.getTask,
  },
  WORKFLOW_DEPENDENCIES: {
    queryFn: WorkflowService.getWorkflowDependencies,
  },
  TAGS: {
    queryFn: TagsService.listTags,
    ...SHELL_DATA,
  },
  INTEGRATIONS: {
    queryFn: IntegrationsService.getIntegrationStatus,
  },
  INTEGRATIONS_CONFIG: {
    queryFn: IntegrationsService.getIntegrationConfig,
  },
  MCP_IDENTITY: {
    queryFn: McpConnectionsService.getMcpIdentity,
  },
  NODES: {
    queryFn: InternalService.getNodes,
    options: {
      // 5 minutes — long enough to dedupe rapid component renders, short
      // enough that registry changes (new nodes, visibility flips) show up
      // without a full hard-refresh round trip.
      staleTime: 5 * 60 * 1000,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  },
  NODE_DISPLAY: {
    queryFn: InternalService.getNodeDisplay,
    options: {
      staleTime: 5 * 60 * 1000,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  },
  MODELS: {
    queryFn: InternalService.getLlms,
    options: {
      staleTime: 30_000,
    },
  },
  WORKFLOW_RUNS: {
    queryFn: RunsService.getWorkflowRuns,
    options: {
      retry: false,
    },
  },
  RUN: {
    queryFn: RunsService.v0GetRun,
    options: {
      retry: false,
    },
  },
  RUN_IO: {
    queryFn: RunsService.getRunIo,
  },
  RUN_WORKFLOW: {
    queryFn: RunsService.getWorkflowForRun,
  },
  ALL_RUNS: {
    queryFn: RunsService.getRuns,
  },
  TYPES_INFORMATION: {
    queryFn: InternalService.getTypes,
  },
  AVAILABLE_TRIGGERS: {
    queryFn: IntegrationsService.getAvailableTriggers,
  },
  TRIGGERS: {
    queryFn: TriggersService.getTriggers,
  },
  TRIGGERS_CONFIG: {
    queryFn: IntegrationsService.getTriggerConfig,
  },
  TRIGGERS_SLACK_INFO: {
    queryFn: IntegrationsService.getSlackInformation,
    options: {
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
  TRIGGER_DEBUG: {
    queryFn: TriggersService.getTriggerEvents,
  },
  GROUP_TRIGGER_EVENTS: {
    queryFn: TriggersService.getGroupTriggerEvents,
  },
  WORKFLOW_IS_VALID: {
    queryFn: WorkflowService.getIsValidWorkflow,
  },
  INVOICES: {
    queryFn: PaymentsService.getAllInvoices,
  },
  BILLING_CURRENCY: {
    queryFn: BillingService.getBillingCurrency,
  },
  BILLING: {
    queryFn: BillingService.getBillingInformation,
  },
  BILLING_TOPUPS: {
    queryFn: BillingService.getPayments,
  },
  BILLING_LIMITS: {
    queryFn: BillingService.getLimits,
  },
  BILLING_LIMIT_PROVIDERS: {
    queryFn: BillingService.getLimitProviders,
  },
  APIKEYS: {
    queryFn: UsersService.getApiKeys,
  },
  APIKEY_ID: {
    queryFn: UsersService.getApiKey,
  },
  INTEGRATIONS_AVAILABLE: {
    queryFn: IntegrationsService.getAvailableIntegrations,
  },
  GROUP: {
    queryFn: (options: Options<GetGroupData, true>) =>
      contextOr("group", options.path.group_id, () =>
        UsersService.getGroup(options)
      ),
    queryKeyPrefix: DATA_KEYS.GROUPS,
    ...SHELL_DATA,
  },
  GROUP_USERS: {
    queryFn: UsersService.listGroupUsers,
  },
  ALL_GROUPS: {
    queryFn: UsersService.getAllGroups,
    queryKeyPrefix: DATA_KEYS.GROUPS,
    ...SHELL_DATA,
  },
  MY_GROUPS: {
    // The context pages the argument-less call; a filtered or sorted one is its own.
    queryFn: (options?: Options<GetMyGroupsData, true>) =>
      options?.query
        ? UsersService.getMyGroups(options)
        : contextOr("my_groups", undefined, () =>
            UsersService.getMyGroups(options)
          ),
    queryKeyPrefix: DATA_KEYS.GROUPS,
    ...SHELL_DATA,
  },
  USERS: {
    queryFn: UsersService.listUsers,
  },
  USER: {
    queryFn: (options?: Options<GetMeData, true>) =>
      contextOr("user", undefined, () => UsersService.getMe(options)),
  },
  USER_ANONYMOUS: {
    queryFn: UsersService.getAnonymousUser,
  },
  USER_DETAILS: {
    queryFn: UsersService.getUserDetails,
  },
  TENANT: {
    queryFn: (options?: Options<GetTenantData, true>) =>
      contextOr("tenant", undefined, () => UsersService.getTenant(options)),
    ...SHELL_DATA,
  },
  BETA_FEATURES: {
    queryFn: (options?: Options<GetEnabledBetaFeaturesData, true>) =>
      contextOr("beta_features", undefined, () =>
        FeatureFlagsService.getEnabledBetaFeatures(options)
      ),
    ...SHELL_DATA,
  },
  BETA_FEATURE_STATES: {
    queryFn: FeatureFlagsService.getBetaFeatureStates,
  },
  GENIE_ASSISTANT: {
    queryFn: AssistantsService.getOrCreateGenieAssistant,
  },
  INVITATION: {
    queryFn: UsersService.getPendingInvite,
  },
  KNOWLEDGE_BASES_V2: {
    queryFn: KnowledgeBasesV2Service.getKnowledgeBasesV2,
  },
  KNOWLEDGE_BASES_TYPES: {
    queryFn: KnowledgeBasesV2Service.fetchKnowledgeBaseCustomTypes,
  },
  KNOWLEDGE_BASES_V2_ID: {
    queryFn: KnowledgeBasesV2Service.getKnowledgeBaseV2,
  },
  KNOWLEDGE_BASE_DOCUMENTS: {
    queryFn: KnowledgeBasesV2Service.getKnowledgeBaseDocuments,
  },
  TABLES: {
    queryFn: TablesService.listTables,
  },
  TABLES_ID: {
    queryFn: TablesService.getTable,
  },
  TABLES_WRITE_SUMMARY: {
    queryFn: TablesService.summarizeTableWrite,
  },
  TABLES_IMPORT_PLAN: {
    queryFn: TablesService.importPlan,
  },
  TABLE_ROWS: {
    queryFn: TablesService.listRows,
  },
  TABLE_STATS: {
    queryFn: TablesService.getTableStats,
  },
  KNOWLEDGE_BASE_RUNNING_JOBS: {
    queryFn: KnowledgeBasesV2Service.getKnowledgeBaseRunningJobs,
  },
  KNOWLEDGE_BASE_INGESTION_JOBS: {
    queryFn: KnowledgeBasesV2Service.getKnowledgeBaseIngestionJobs,
  },
  AZURE_INTEGRATION_NANGO_USERNAME: {
    queryFn: IntegrationsService.getOnedriveUserInformation,
  },
  FILE_METADATA: {
    queryFn: UploadService.getImageMetadata,
  },
  ASSISTANTS: {
    queryFn: AssistantsService.getAssistants,
  },
  ASSISTANT_ID: {
    queryFn: AssistantsService.getAssistant,
  },
  ASSISTANT_TOOLS: {
    queryFn: AssistantsService.listAssistantTools,
  },
  // A read-only POST: what a change Genie proposes would do, for its card.
  ASSISTANT_CHANGES: {
    queryFn: AssistantsService.previewAssistantChanges,
  },
  // The same for an agent Genie proposes to create.
  NEW_ASSISTANT_CHANGES: {
    queryFn: AssistantsService.previewNewAssistantChanges,
  },
  ANALYTICS_TENANT_METADATA: {
    queryFn: AnalyticsService.getTenantAnalyticsItems,
  },
  ANALYTICS_BILLING_METADATA: {
    queryFn: AnalyticsService.getBillingAnalyticsItems,
  },
  ANALYTICS_CHAT_METADATA: {
    queryFn: AnalyticsService.getChatAnalyticsItems,
  },
  ANALYTICS_WORKFLOW_METADATA: {
    queryFn: AnalyticsService.getWorkflowAnalyticsItems,
  },
  ANALYTICS_KB_METADATA: {
    queryFn: AnalyticsService.getKbAnalyticsItems,
  },
  ANALYTICS_WORKSPACE: {
    queryFn: AnalyticsService.getWorkspaceAnalyticsItems,
  },
  TENANT_ANALYTICS: {
    queryFn: AnalyticsService.getTenantAnalytics,
  },
  ASSISTANT_ANALYTICS: {
    queryFn: AnalyticsService.getAssistantAnalytics,
  },
  KB_ANALYTICS: {
    queryFn: AnalyticsService.getKbAnalytics,
  },
  INFRASTRUCTURE_ANALYTICS: {
    queryFn: AnalyticsService.getInfrastructureAnalytics,
  },
  API_ANALYTICS_METADATA: {
    queryFn: AnalyticsService.getApiAnalyticsItems,
  },
  INFRASTRUCTURE_ANALYTICS_METADATA: {
    queryFn: AnalyticsService.getInfrastructureAnalyticsItems,
  },
  WORKFLOW_LOG_COLUMNS: {
    queryFn: WorkflowService.getWorkflowLogColumns,
  },
  LABELLING_TASKS: {
    queryFn: LabellingTasksService.getLabellingTasks,
  },
  WORKSHOP_RECENT: {
    queryFn: WorkflowService.getWorkshopRecent,
  },
  PROVIDERS: {
    queryFn: ProvidersService.listProviders,
  },
  PLUGINS: {
    queryFn: PluginsService.listPlugins,
  },
  PLUGIN_CATALOG: {
    queryFn: PluginsService.listPluginCatalog,
  },
  MARKETPLACE_PLUGINS: {
    queryFn: PluginsService.listMarketplacePlugins,
  },
  PLUGINS_MISSING_CONFIG_CHECK: {
    queryFn: PluginsService.listPlugins,
  },
  APPS: {
    queryFn: AppsService.listApps,
  },
  ASSISTANT_VERSIONS: {
    queryFn: AssistantsService.getAssistantVersions,
  },
  ASSISTANT_VERSION: {
    queryFn: AssistantsService.getAssistantVersion,
  },
  NODE_INPUTS: {
    queryFn: WorkflowService.getWorkflowNodeInputs,
  },
  NODE_LAST_INPUTS: {
    queryFn: WorkflowService.getNodeLastInputs,
  },
  ASSISTANT_TRIGGER_CONFIG: {
    queryFn: AssistantsService.getAssistantTriggerConfig,
  },
  DEPLOYMENT_CONFIG: {
    queryFn: DeploymentsService.getDeploymentConfig,
  },
  DEPLOYMENT_APP: {
    queryFn: DeploymentsService.getDeploymentApp,
    options: {
      retry: false,
    },
  },
  DEPLOYMENT_APP_BY_ALIAS: {
    queryFn: DeploymentsService.getDeploymentAppByAlias,
    options: {
      retry: false,
    },
  },
  LIST_DEPLOYMENTS: {
    queryFn: DeploymentsService.listDeployments,
  },
  ASSISTANT_TRIGGER_CUSTOMIZATION: {
    queryFn: AssistantsService.getAssistantTriggerCustomization,
  },
  APP_CONFIG: {
    queryFn: AppsService.getAppConfig,
    options: {
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
  MIME_TYPES: {
    queryFn: KnowledgeBasesV2Service.getCompatibleMimeTypes,
    options: {
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
  SERVER_SETTINGS: {
    queryFn: AdminService.getServerSettings,
    options: {
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
  KB_HEALTH_MANIFEST: {
    queryFn: KnowledgeBasesV2Service.kbHealthManifest,
    options: {
      // Manifest is static per-deploy; refetch only on explicit
      // invalidation. Adding a new check requires a backend deploy
      // anyway, so refetching every modal-open is wasted work.
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
  EVALUATORS: {
    queryFn: EvaluationService.listEvaluators,
  },
  EVALUATOR: {
    queryFn: EvaluationService.getEvaluator,
  },
  EVALUATOR_DEFINITIONS: {
    queryFn: EvaluationService.getEvaluatorDefinitions,
    options: {
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  },
  WORKFLOW_EVALUATOR_DEFINITIONS: {
    queryFn: EvaluationService.getWorkflowEvaluatorDefinitions,
    options: {
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  },
  AGENT_EVALUATOR_DEFINITIONS: {
    queryFn: EvaluationService.getAgentEvaluatorDefinitions,
    options: {
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  },
  TEST_CASES: {
    queryFn: EvaluationService.listTestCases,
    options: {
      staleTime: 30_000,
    },
  },
  TEST_CASE: {
    queryFn: EvaluationService.getTestCase,
  },
  TEST_CASE_RUNS: {
    queryFn: EvaluationService.getTestCaseRuns,
  },
  TEST_SUITES: {
    queryFn: EvaluationService.listWorkflowTestSuites,
    options: {
      staleTime: 30_000,
    },
  },
  TEST_SUITE_EVALUATION_RESULTS: {
    queryFn: EvaluationService.getSuiteEvaluationResults,
  },
  TEST_SUITE: {
    queryFn: EvaluationService.getTestSuite,
  },
  AGENT_TEST_SUITES: {
    queryFn: EvaluationService.listAgentTestSuites,
  },
  EVALUATION_RUNS: {
    queryFn: EvaluationService.listEvaluationRuns,
  },
  EVALUATION_RUN_REPORT: {
    queryFn: EvaluationService.getEvaluationRunReport,
  },
  TOOL_METADATA_SCHEMAS: {
    queryFn: InternalService.getToolMetadataSchemas,
  },
  STORAGE_STATUS: {
    queryFn: (options?: Options<StorageStatusData, true>) =>
      contextOr("storage", undefined, () =>
        StatusService.storageStatus(options)
      ),
    options: {
      // Polled so a workspace hears about a filling disk before a request is
      // refused; slow, because disks fill over hours.
      refetchInterval: 600_000,
      staleTime: 300_000,
    },
  },
  HEURISTICS_EVALUATION: {
    queryFn: runHeuristicEvaluationJob,
    // Job polling honors AbortSignal — abandon stale polls on unmount/supersede
    passSignal: true,
    options: {
      staleTime: 1000 * 60 * 10, // 10 minutes - same prompt results don't change
      retry: 1,
    },
  },
  ROLES_ORGANIZATION: {
    queryFn: RolesService.listTenantRoles,
  },
  ROLES_WORKSPACE: {
    queryFn: RolesService.listGroupRoles,
  },
  MY_PERMISSIONS: {
    queryFn: (options?: Options<GetMyPermissionsData, true>) =>
      contextOr("permissions", options?.query?.group_id ?? "", () =>
        PermissionsService.getMyPermissions(options)
      ),
  },
  INSIGHTS_TOP_TOPICS: {
    queryFn: InsightsService.getTopTopics,
  },
  INSIGHTS_NOTICED: {
    queryFn: InsightsService.getNoticed,
  },
  INSIGHTS_SENTIMENT: {
    queryFn: InsightsService.getSentimentOverTime,
  },
  INSIGHTS_CSAT: {
    queryFn: InsightsService.getCsatScore,
  },
  INSIGHTS_RATING_DRIVERS: {
    queryFn: InsightsService.getRatingDrivers,
  },
  INSIGHTS_CONVERSATION_FUNNEL: {
    queryFn: InsightsService.getConversationFunnel,
  },
  INSIGHTS_BOOTSTRAP: {
    queryFn: InsightsService.getBootstrapStatus,
  },
  INSIGHTS_CUSTOM: {
    queryFn: InsightsService.getCustomInsights,
  },
  INSIGHTS_CALCULATING: {
    queryFn: InsightsService.getCalculatingInsights,
  },
  INSIGHTS_CONVERSATIONS: {
    queryFn: InsightsService.getInsightConversations,
  },
} as const;

type QueryConfigs = typeof queryConfigs;

type QueryFnType<T extends keyof QueryConfigs> = QueryConfigs[T]["queryFn"];
type QueryConfigData<T extends keyof QueryConfigs> = Awaited<
  ReturnType<QueryFnType<T>>
>["data"];
type QueryConfigParams<T extends keyof QueryConfigs> = Parameters<
  QueryFnType<T>
>;

export function useRemoteData<T extends keyof QueryConfigs>(
  key: T,
  ...args: QueryConfigParams<T> extends []
    ? [options?: Partial<UseQueryOptions<QueryConfigData<T>>>]
    : undefined extends QueryConfigParams<T>[0]
      ? [
          args?: QueryConfigParams<T>[0],
          options?: Partial<UseQueryOptions<QueryConfigData<T>>>,
        ]
      : [
          args: QueryConfigParams<T>[0],
          options?: Partial<UseQueryOptions<QueryConfigData<T>>>,
        ]
) {
  const config = queryConfigs[key];

  if (!config) {
    throw new Error(`No query configuration found for key: ${key}`);
  }

  const hasArgs = (config.queryFn.length ?? 0) > 0;
  let argsValue: any;
  let options: any;

  if (hasArgs) {
    argsValue = args[0];
    options = args[1];
  } else {
    options = args[0];
  }

  //@ts-ignore queryKeyPrefix might not exist
  const queryKey = config.queryKeyPrefix
    ? //@ts-ignore queryKeyPrefix might not exist
      [config.queryKeyPrefix, key, args[0]]
    : [key, args[0]];

  const defaultOptions = {
    retryDelay: (attemptIndex: number) =>
      Math.min(1000 * 2 ** attemptIndex, 30000),
    retry: retriesRequest(2),
  };

  return useQuery<QueryConfigData<T>>({
    queryKey: queryKey,
    // Only read context.signal for opted-in keys: accessing it marks the
    // query cancellable in React Query, changing unmount behavior.
    queryFn: (context) =>
      dataWrapper<QueryConfigData<T>>(config.queryFn)(
        ...(argsValue !== undefined
          ? //@ts-ignore passSignal might not exist
            [
              //@ts-ignore passSignal might not exist
              config.passSignal
                ? { ...argsValue, signal: context.signal }
                : argsValue,
            ]
          : [])
      ),
    ...defaultOptions,
    //@ts-ignore options might not exist
    ...(config.options ? config.options : {}),
    ...options,
    // Before the workspace loads its id is empty, and that request 404s.
    ...(argsValue?.path?.group_id === "" ? { enabled: false } : {}),
  });
}

export function prefetchQuery<T extends keyof QueryConfigs>(
  client: QueryClient,
  key: T,
  ...args: QueryConfigParams<T> extends []
    ? [options?: Partial<UseQueryOptions<QueryConfigData<T>>>]
    : undefined extends QueryConfigParams<T>[0]
      ? [
          args?: QueryConfigParams<T>[0],
          options?: Partial<UseQueryOptions<QueryConfigData<T>>>,
        ]
      : [
          args: QueryConfigParams<T>[0],
          options?: Partial<UseQueryOptions<QueryConfigData<T>>>,
        ]
) {
  const config = queryConfigs[key];

  if (!config) {
    throw new Error(`No query configuration found for key: ${key}`);
  }

  const hasArgs = (config.queryFn.length ?? 0) > 0;
  let argsValue: any;
  let options: any;

  if (hasArgs) {
    argsValue = args[0];
    options = args[1];
  } else {
    options = args[0];
  }

  //@ts-expect-error queryKeyPrefix might not exist
  const queryKey = config.queryKeyPrefix
    ? //@ts-ignore queryKeyPrefix might not exist
      [config.queryKeyPrefix, key, args[0]]
    : [key, args[0]];

  return client.prefetchQuery<QueryConfigData<T>>({
    queryKey: queryKey,
    queryFn: () =>
      dataWrapper<QueryConfigData<T>>(config.queryFn)(
        ...(argsValue !== undefined ? [argsValue] : [])
      ),
    //@ts-ignore options might not exist
    ...(config.options ? config.options : {}),
    ...options,
  });
}
